// StopSheet — "Why are you stopping?", declared ONCE for both screens that
// can stop the clock.
//
// Field has two Clock out buttons: the LiveCard on Today and the sticky
// control at the bottom of a job. #545 put the reason prompt in front of the
// first one and left the second sending `note: ''`, which is the whole of the
// bug it was fixing — `server/routes/field.js` applyOp only tells dispatch
// about a stop when a reason came with it, so every stop made from the job
// screen (the screen a crew member is already on, because the work list is
// there) stayed as silent as before.
//
// So it lives here rather than in a screen. A second copy is how the two
// diverge again, and the failure mode is invisible: both screens still stop
// the clock, and only one of them tells anybody why.
//
// Two rules the inline version got wrong, both about a stop that DOES NOT go
// through:
//
//   · the reason is kept. A refusal used to close the sheet and clear the box,
//     so a crew member with one bar typed their sentence again for every
//     retry. It is cleared only after the server has taken it.
//   · the refusal is rendered INSIDE the sheet. Both screens show their errors
//     in the page behind it, which an open sheet covers — so keeping the sheet
//     open without moving the message would have replaced a lost sentence with
//     a silent failure.
//
// `onStop(reason)` resolves the sentence to show, or null/undefined when the
// stop was accepted (a queued stop counts as accepted — the phone has it).
//
// ── CLOCKING OUT MUST NOT REQUIRE A KEYBOARD ──────────────────────────────
//
// Reported 2026-09-18: "iphone workers can't clock out". The reason box is
// required, so stopping the clock meant tapping into a textarea — and on iOS
// the keyboard that opens covers the bottom of the layout viewport, which is
// where this sheet's confirm button lives. Sheet.js now tracks
// visualViewport so the button stays above the keyboard; that is the fix for
// the geometry.
//
// This is the fix for the REQUIREMENT, and it is the one that matters more.
// The commonest reasons are four taps' worth of typing on a phone held in one
// hand, outdoors, in gloves, by somebody who has finished for the day. Offering
// them as buttons means the normal case is TWO taps and no keyboard at all —
// so the geometry stops being load-bearing rather than merely being correct.
// Same shape as Problem.js's severities: a chosen state carried by weight, a
// tick AND aria-pressed, never colour alone.
//
// The box stays, because "whatever it is" is the point of asking, and anything
// typed replaces the pick.

import React, { useState } from 'react';
import { FwButton, Notice, Sheet, FwField, FwTextarea, SectionLabel } from '../../ui/fw';
import './StopSheet.css';

export const STOP_REASON_ID = 'fd-stop-reason';

/**
 * The reasons a crew member actually gives, as buttons.
 *
 * Short enough to read at a glance and to fit two-up at 320px. They are
 * SENTENCES the office reads in the dispatch feed, not codes — `applyOp` puts
 * this straight in front of a dispatcher, so "Finished" is the whole message.
 */
export const STOP_REASONS = [
  'Finished',
  'Taking a break',
  'Moving to another job',
  'Waiting on access',
  'Weather stopped us',
  'Out of daylight',
];

// `title`, `intro` and `confirmLabel` exist for ONE other caller: switching
// jobs (screens/Job.js). Clocking into a job while on another stops the first,
// and classic asked why before it would — so the same sheet asks, with words
// that say what the button will do. The reasons, the box and the in-sheet
// refusal are this sheet's, unchanged, so the two cannot drift.
export default function StopSheet({
  open, busy = false, onClose, onStop,
  title = 'Why are you stopping?', intro = null, confirmLabel = 'Clock out', confirmAria = 'Stop the clock',
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const close = () => {
    setError(null);
    if (onClose) onClose();
  };

  const confirm = async () => {
    const text = reason.trim();
    // Unreachable from the UI — the confirm below is `disabled` until there is
    // something to send — and kept as the half that makes "spaces are not a
    // reason" true if the attribute is ever dropped. Recorded rather than
    // claimed as covered; see the note in Today.test.js.
    if (!text) return;
    setError(null);
    const refusal = await onStop(text);
    if (refusal) { setError(refusal); return; }
    setReason('');
    close();
  };

  return (
    <Sheet
      open={open}
      title={title}
      onClose={close}
      footer={(
        <>
          <FwButton variant="quiet" onClick={close} disabled={busy}>Cancel</FwButton>
          <FwButton
            variant="stop"
            onClick={confirm}
            loading={busy}
            disabled={!reason.trim()}
            aria-label={confirmAria}
          >
            {confirmLabel}
          </FwButton>
        </>
      )}
    >
      {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
      {intro ? <p className="fd-hint" data-stop-intro>{intro}</p> : null}
      {/* Before the box, because for most stops it is the whole answer and the
          keyboard never has to open. */}
      <SectionLabel as="h3">Tap one</SectionLabel>
      <div className="fd-stop__picks">
        {STOP_REASONS.map((r) => {
          const chosen = reason === r;
          return (
            <FwButton
              key={r}
              // `ghost`, not `secondary` — which is not a variant. FwButton
              // falls back to `primary` for anything it does not know, so all
              // six picks rendered brand-filled and identical, and the only
              // thing separating chosen from unchosen was the tick. Caught in
              // review; buttonVariants.test.js now refuses an unknown name.
              variant={chosen ? 'primary' : 'ghost'}
              // The tick as an ICON rather than text: .fw-btn__icon is
              // `flex: none` and OUTSIDE .fw-btn__label, so choosing a reason
              // cannot push its own label over the truncation edge.
              icon={chosen ? 'check' : undefined}
              // Not a stylesheet reaching into .fw-btn__label — the primitive
              // owns this now (fw-btn--wrap).
              wrap
              className="fd-stop__pick"
              data-stop-reason={r}
              aria-pressed={chosen}
              disabled={busy}
              onClick={() => setReason(chosen ? '' : r)}
            >
              {r}
            </FwButton>
          );
        })}
      </div>
      <FwField
        label="Or say it yourself"
        htmlFor={STOP_REASON_ID}
        hint="Dispatch is told this, whichever way you give it."
      >
        <FwTextarea
          id={STOP_REASON_ID}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="Finished the front beds"
          maxLength={2000}
        />
      </FwField>
    </Sheet>
  );
}
